import React, { useEffect, useState, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { supabase } from '../supabaseClient';

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [editItem, setEditItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('bookings')
      .select('*')
      .range((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage - 1);

    if (error) {
      console.error(error);
    } else {
      setBookings(data);
    }
    setLoading(false);
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const deleteItem = async (id) => {
    const { error } = await supabase
      .from('bookings')
      .delete()
      .eq('id', id);

    if (error) {
      console.error(error);
    } else {
      fetchBookings();
    }
  };

  const updateItem = async () => {
    if (editItem) {
      setSaving(true);
      const { error } = await supabase
        .from('bookings')
        .update(editItem)
        .eq('id', editItem.id);

      setSaving(false);

      if (error) {
        console.error(error);
      } else {
        fetchBookings();
        setEditItem(null);
        toast.success('Data successfully updated');
      }
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

    const filteredBookings = bookings.filter(
    (item) =>
      item.businessaddress?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.date?.toLowerCase().includes(searchQuery.toLowerCase())
  );

return (
    <div className="container pb-0 mb-0">
      <h3 className="mb-4">Bookings</h3>
      <input
        type="text"
        placeholder="Search by Business Address, Client Full Name, or Date"
        className="form-control"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="table-responsive" style={{width: '78vw', height: '52vh'}}>
          <table className="table table-hover">
            <thead>
              <tr>
                <th>ID</th>
                <th>Business Name</th>
                <th>Business Location</th>
                <th>Business Address</th>
                <th>Date</th>
                <th>Time</th>
                <th>Amount Received</th>
                <th>Service</th>
                <th>Location</th>
                <th>Client Address</th>
                <th>Status</th>
                <th>Client Full Name</th>
                <th>Worker</th>
                <th>Service Cost</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td className="font-weight-bold">{item.title}</td>
                  <td><strong className="font-weight-bold text-primary">Latitude: </strong>{item.latitude}, <strong className="font-weight-bold text-primary">Longitude: </strong>{item.longitude}</td>
                  <td>{item.businessaddress}</td>
                  <td>{item.date}</td>
                  <td>{item.time}</td>
                  <td>{item.total}</td>
                  <td>{item.service}</td>
                  <td>{item.location}</td>
                  <td>{item.clientaddress}</td>
                  <td className={item.status === 'Pending' ? 'text-danger' : 'text-success'}>
                    {item.status}
                  </td>
                  <td>{item.full_name}</td>
                  <td>{item.worker}</td>
                  <td>{item.mainTotal}</td>
                  <td className="gap-2 d-flex justify-content-center mt-4">
                    <button
                      className="btn btn-primary me-2"
                      onClick={() => setEditItem(item)}
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => deleteItem(item.id)}
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="d-flex justify-content-center mt-4">
        <button
          className="btn btn-secondary me-2"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Next
        </button>
      </div>

      {editItem && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Item</h5>
                <button type="button" className="btn-close" onClick={() => setEditItem(null)}></button>
              </div>
              <div className="modal-body">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    updateItem();
                  }}
                >
                  <div className="mb-3">
                    <label className="form-label">Company Location</label>
                    <input
                      type="text"
                      className="form-control"
                      value={`${editItem.latitude}, ${editItem.longitude}`}
                      onChange={(e) => {
                        const [latitude, longitude] = e.target.value.split(', ').map(Number);
                        setEditItem({ ...editItem, latitude, longitude });
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Business Address</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editItem.businessaddress}
                      onChange={(e) =>
                        setEditItem({ ...editItem, businessaddress: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={editItem.date}
                      onChange={(e) =>
                        setEditItem({ ...editItem, date: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Time</label>
                    <input
                      type="time"
                      className="form-control"
                      value={editItem.time}
                      onChange={(e) =>
                        setEditItem({ ...editItem, time: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Total</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editItem.total}
                      onChange={(e) =>
                        setEditItem({ ...editItem, total: parseFloat(e.target.value) })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Service</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editItem.service}
                      onChange={(e) =>
                        setEditItem({ ...editItem, service: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Location</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editItem.location}
                      onChange={(e) =>
                        setEditItem({ ...editItem, location: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Client Address</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editItem.clientaddress}
                      onChange={(e) =>
                        setEditItem({ ...editItem, clientaddress: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editItem.status}
                      onChange={(e) =>
                        setEditItem({ ...editItem, status: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editItem.title}
                      onChange={(e) =>
                        setEditItem({ ...editItem, title: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editItem.full_name}
                      onChange={(e) =>
                        setEditItem({ ...editItem, full_name: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Worker</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editItem.worker}
                      onChange={(e) =>
                        setEditItem({ ...editItem, worker: e.target.value })
                      }
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Main Total</label>
                    <input
                      type="number"
                      className="form-control"
                      value={editItem.mainTotal}
                      onChange={(e) =>
                        setEditItem({ ...editItem, mainTotal: parseFloat(e.target.value) })
                      }
                    />
                  </div>
                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-secondary me-2"
                      onClick={() => setEditItem(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? 'Loading...' : 'Save'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default Bookings;
